import type { CSSProperties } from 'react';
import {
  isActiveHomepageDeal,
  type HomepageSection,
  type HomepageSectionResponse,
} from '@/lib/homepage-sections';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';

export const HOMEPAGE_LEADING_SPACE =
  'pb-6 pt-12 sm:pb-8 sm:pt-16 md:pb-10 md:pt-20 lg:pb-12 lg:pt-24';
export const HOMEPAGE_TRAILING_SPACE =
  'pb-12 pt-6 sm:pb-16 sm:pt-8 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12';

function Block({ className = '' }: { className?: string }) {
  return <div className={`rounded-lg bg-muted motion-safe:animate-pulse ${className}`} />;
}

// Match the real card's square image, 47px title, price line and colour row.
// No product subscriptions, commerce providers or interactive fake controls.
function ProductCardSkeleton() {
  return (
    <div className="flex w-full flex-col rounded-[12px] bg-[#F9FAFB]">
      <div className="w-full px-[6px] py-[6px] md:px-4 md:py-3">
        <Block className="aspect-square w-full rounded-[12px]" />
      </div>
      <div className="flex w-full flex-col gap-3 p-3 md:gap-4 md:p-4">
        <div className="flex h-[47px] w-full flex-col justify-center gap-2">
          <Block className="h-4 w-full" />
          <Block className="h-4 w-3/4" />
        </div>
        <div data-ea-visual-tag="h3" className="relative h6 font-semibold sm:h5">
          &nbsp;
          <Block className="absolute inset-y-1 left-0 w-24" />
        </div>
        <div className="h-[23px] w-full" />
      </div>
    </div>
  );
}

function BlogCardSkeleton() {
  return (
    <div className="col-span-1 flex w-full flex-col items-start gap-4 md:flex-row md:items-center lg:gap-6">
      <Block className="h-[210px] w-full rounded-[12px] md:max-w-[332px] lg:h-[190px]" />
      <div className="flex w-full flex-col items-start gap-3 lg:gap-4">
        <div className="relative h6 w-3/4 font-semibold">
          <span>&nbsp;</span>
          <Block className="absolute inset-y-1 w-full" />
        </div>
        <div className="relative h5 w-full font-semibold tracking-tighter">
          &nbsp;
          <br />
          &nbsp;
          <Block className="absolute inset-y-1 w-full" />
        </div>
        <Block className="h-12 w-[160px] rounded-full" />
      </div>
    </div>
  );
}

export function HomepageSectionSkeleton({
  section,
  title,
  description = '',
  result,
  dealIndex = 0,
}: {
  section: HomepageSection;
  title: string;
  description?: string;
  result?: HomepageSectionResponse;
  dealIndex?: 0 | 1;
}) {
  const count = result?.section === section ? result.data.length : undefined;
  if (count === 0 && section !== 'categories' && section !== 'blogs' && section !== 'icons')
    return null;

  switch (section) {
    case 'top-products':
      return (
        <div
          data-homepage-skeleton={section}
          className={`custom-section pr-0 ${HOMEPAGE_LEADING_SPACE}`}
        >
          <div className="custom-container flex flex-col items-start gap-6 sm:flex-row sm:items-center md:gap-12 lg:gap-[140px]">
            <div className="flex w-full flex-col items-start gap-8 sm:max-w-[332px]">
              <div className="flex w-full flex-col gap-3 sm:gap-4">
                <Heading title={title} />
                {description && <Paragraph>{description}</Paragraph>}
              </div>
              <div aria-hidden="true" className="hidden gap-2 sm:flex">
                <Block className="h-12 w-12 rounded-full" />
                <Block className="h-12 w-12 rounded-full" />
              </div>
            </div>
            <div aria-hidden="true" className="w-full overflow-hidden">
              <div className="flex w-full gap-4 sm:gap-5 lg:gap-6">
                {Array.from({ length: count ?? 8 }, (_, i) => (
                  <div key={i} className="w-full max-w-[300px] shrink-0 md:max-w-[332px]">
                    <ProductCardSkeleton />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    case 'best-products':
      return (
        <div
          data-homepage-skeleton={section}
          className={`custom-section section-gap pr-0 sm:px-5 ${HOMEPAGE_TRAILING_SPACE}`}
        >
          <Heading center className={{ container: 'w-full max-w-[800px]' }} title={title} />
          <div aria-hidden="true" className="custom-container w-full overflow-hidden">
            <div className="flex w-full sm:grid sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
              {Array.from({ length: count ?? 8 }, (_, i) => (
                <div key={i} className="mx-2 w-full max-w-[300px] shrink-0 sm:mx-0 sm:max-w-none">
                  <ProductCardSkeleton />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'icons': {
      const iconCount =
        result?.section === 'icons'
          ? result.data.filter((icon) => icon.type?.includes('homepage')).length
          : 4;
      return (
        <div
          aria-hidden="true"
          data-homepage-skeleton={section}
          className="custom-section py-6 sm:py-8 md:py-10 lg:py-12"
        >
          <div className="custom-container grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 xl:grid-cols-4">
            {Array.from({ length: iconCount }, (_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-[12px] bg-secondary-foreground p-3 md:gap-4"
              >
                <Block className="h-16 w-16 shrink-0 rounded-[10px]" />
                <div className="flex w-full flex-col gap-2">
                  <Block className="h-5 w-3/4" />
                  <Block className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case 'categories': {
      const categories = result?.section === 'categories' ? result.data : undefined;
      const banners = categories
        ? categories.filter(
            (category) => category.type_flags?.includes('highlight') && category.banners?.length,
          ).length
        : 1;
      const other = categories
        ?.filter((category) => !category.type_flags?.includes('highlight'))
        .slice(0, 12);
      // Neutral chip widths approximate the current menu until real labels arrive;
      // once data is present, invisible real labels reserve their exact wrapping.
      const chipWidths = [252, 236, 240, 180, 204, 112];
      return (
        <div
          data-homepage-skeleton={section}
          className="custom-section gap-6 py-6 sm:gap-8 sm:py-8 md:gap-10 md:py-10 lg:gap-12 lg:py-12"
        >
          <Heading center title={title} />
          <div
            aria-hidden="true"
            className="custom-container grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4 xl:gap-6"
          >
            {Array.from({ length: banners }, (_, i) => (
              <Block
                key={i}
                className="aspect-[341/224] w-full rounded-[12px] md:aspect-[332/380]"
              />
            ))}
          </div>
          <div className="custom-container flex flex-col items-center gap-3 sm:gap-4">
            <Heading center title="Ďalšie kategórie" tag="p" styleAs="h5" />
            <div
              aria-hidden="true"
              className="flex w-full flex-wrap items-center justify-center gap-1 sm:gap-1.5 lg:gap-2"
            >
              {other
                ? other.map((category) => (
                    <div
                      key={category.id}
                      className="flex h-12 items-center rounded-full bg-muted px-4 text-sm font-semibold tracking-tight motion-safe:animate-pulse sm:px-5 sm:text-base lg:px-6"
                    >
                      <span className="invisible">{category.name}</span>
                    </div>
                  ))
                : chipWidths.map((width, i) => (
                    <div
                      key={i}
                      className="h-12 max-w-full rounded-full bg-muted motion-safe:animate-pulse w-[var(--chip-width)] sm:w-[var(--chip-width-lg)]"
                      style={
                        {
                          '--chip-width': `${width}px`,
                          '--chip-width-lg': `${width * 1.25}px`,
                        } as CSSProperties
                      }
                    />
                  ))}
            </div>
          </div>
        </div>
      );
    }
    case 'blogs':
      return (
        <div
          data-homepage-skeleton={section}
          className="custom-section section-gap bg-[#F9FAFB] py-12 sm:py-16 md:py-20 lg:py-24"
        >
          <div className="custom-container flex items-center justify-between gap-4">
            <Heading title={title} />
            <div aria-hidden="true" className="hidden sm:block">
              <Block className="h-12 w-[180px] rounded-full" />
            </div>
          </div>
          <div
            aria-hidden="true"
            className="custom-container grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            {Array.from({ length: count ?? 4 }, (_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        </div>
      );
    case 'stores':
      return (
        <div
          aria-hidden="true"
          data-homepage-skeleton={section}
          className={`custom-section ${HOMEPAGE_LEADING_SPACE}`}
        >
          <div className="custom-container flex flex-col items-start gap-4 md:flex-row md:items-stretch md:justify-between lg:gap-6">
            <Block className="h-[400px] w-full max-w-[925px] rounded-[12px] sm:h-[430px] lg:h-[500px]" />
            <Block className="h-[200px] w-full rounded-[12px] sm:h-[350px] md:h-auto md:max-w-[451px]" />
          </div>
        </div>
      );
    case 'deals': {
      // Optional promotions must not create a 240px hole which disappears when
      // the API confirms there is no active deal. Warm their query further ahead.
      const deal = result?.section === 'deals' ? result.data[dealIndex] : undefined;
      if (!isActiveHomepageDeal(deal)) return null;
      return (
        <div
          aria-hidden="true"
          data-homepage-skeleton={section}
          className={`custom-section ${dealIndex === 1 ? 'py-6 sm:py-8 md:py-10' : HOMEPAGE_TRAILING_SPACE}`}
        >
          <div className="custom-container flex flex-col gap-6 md:flex-row lg:gap-8">
            <Block
              className={`aspect-square w-full ${dealIndex === 1 ? 'md:max-w-[300px]' : 'md:max-w-[720px]'}`}
            />
            <div className="flex w-full flex-col justify-center gap-6">
              <Block className="h-12 w-3/4" />
              <Block className="h-20 w-full" />
              <Block className="h-12 w-40 rounded-full" />
            </div>
          </div>
        </div>
      );
    }
  }
}

export function HomepageReviewsSkeleton({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div
      data-homepage-skeleton="reviews"
      className={`custom-section section-gap px-0 md:px-5 ${HOMEPAGE_TRAILING_SPACE}`}
    >
      <div className="custom-container flex flex-col items-center gap-3 px-5 sm:gap-4 md:px-0">
        <Heading title={title} center />
        <Paragraph color="black" className="text-center">
          {description}
        </Paragraph>
        <div aria-hidden="true" className="w-full sm:w-[420px]">
          <Block className="h-9 w-full rounded-full" />
        </div>
      </div>
      <div
        aria-hidden="true"
        className="custom-container hidden h-[700px] grid-cols-3 gap-6 overflow-hidden md:grid"
      >
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex flex-col gap-6">
            <Block className="h-[240px] w-full shrink-0" />
            <Block className="h-[240px] w-full shrink-0" />
            <Block className="h-[240px] w-full shrink-0" />
          </div>
        ))}
      </div>
      <div
        aria-hidden="true"
        className="flex h-[220px] w-full gap-4 overflow-hidden sm:h-[270px] md:hidden"
      >
        {Array.from({ length: 3 }, (_, i) => (
          <Block key={i} className="h-full min-w-[280px] max-w-[280px] shrink-0 rounded-[12px]" />
        ))}
      </div>
      <div aria-hidden="true" className="custom-container flex justify-center px-5 md:px-0">
        <Block className="h-12 w-[260px] rounded-full" />
      </div>
    </div>
  );
}
